import React from "react";
import SectionTitle from "../common/SectionTitle";
import SubHeading from "../common/SubHeading";
import { useFormSection, FormSectionProps } from "../../hooks/useFormSection";
import type { FamilyHistoryEntry } from "../../lib/types/IntakeFormTypes";

const yesNoOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" }
];

const yesNoComboOptions = [
  ...yesNoOptions,
  { label: "Combo", value: "combo" }
];

const PastHealthMedicalHistorySectionV2: React.FC<FormSectionProps> = (props) => {
  const { Field, formData, isEditMode = true } = useFormSection(props);
  
  // Access nested data for conditional rendering
  const preExistingData = formData?.preExistingConditions || {};

  const handleFamilyHistoryChange = (
    history: FamilyHistoryEntry[],
    index: number,
    field: keyof FamilyHistoryEntry,
    value: string
  ) => {
    const newHistory = [...history];
    newHistory[index] = { ...newHistory[index], [field]: value };
    return newHistory;
  };

  const addFamilyMember = (history: FamilyHistoryEntry[]) => {
    return [...history, { member: "", age: "", status: "" }];
  };

  const removeFamilyMember = (history: FamilyHistoryEntry[], index: number) => {
    return history.filter((_, i) => i !== index);
  };

  return (
    <section>
      <SectionTitle>Pre-Existing Conditions & Medical History</SectionTitle>

      <SubHeading>Pre-Existing Health Conditions</SubHeading>
      
      <Field
        path="preExistingConditions.hasConditions"
        label="Do you have any known current or past health conditions?"
        type="radio"
        options={yesNoOptions}
      />
      
      {preExistingData.hasConditions === "yes" && (
        <Field
          path="preExistingConditions.conditionsDetails"
          label="If yes, please explain"
          type="textarea"
          rows={3}
        />
      )}
      
      <Field
        path="preExistingConditions.hasAllergies"
        label="Do you have any food allergies?"
        type="radio"
        options={yesNoOptions}
      />
      
      {preExistingData.hasAllergies === "yes" && (
        <Field
          path="preExistingConditions.allergiesDetails"
          label="If yes, please list the foods"
          type="textarea"
          rows={3}
        />
      )}
      
      <Field
        path="preExistingConditions.hasSensitivities"
        label="Do you have any food sensitivities?"
        type="radio"
        options={yesNoOptions}
      />
      
      {preExistingData.hasSensitivities === "yes" && (
        <Field
          path="preExistingConditions.sensitivitiesDetails"
          label="If yes, please list the foods"
          type="textarea"
          rows={3}
        />
      )}
      
      <Field
        path="preExistingConditions.testedForSensitivities"
        label="Have you been tested for food sensitivities or is the above based on reaction to the food consumed?"
        type="textarea"
        rows={3}
      />

      <SubHeading>Family/Medical History</SubHeading>
      
      <Field
        path="familyMedicalHistory.children"
        label="Do you have children? What are their ages"
        type="textarea"
        rows={2}
      />
      
      <Field
        path="familyMedicalHistory.lifeChanges"
        label="Have you or your family experienced any significant recent life changes? Please explain"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="familyMedicalHistory.losses"
        label="Have you experienced any major losses in life? Please explain"
        type="textarea"
        rows={3}
      />
      
      <div className="my-6">
        <label className="block text-brand-stone-700 mb-2 font-medium">
          Daily Stressors: Rate each on a scale of 1-10
        </label>
        <div className="space-y-4">
          <Field
            path="familyMedicalHistory.stressors.family"
            label="Family"
            type="slider"
            min={0}
            max={10}
          />
          <Field
            path="familyMedicalHistory.stressors.work"
            label="Work"
            type="slider"
            min={0}
            max={10}
          />
          <Field
            path="familyMedicalHistory.stressors.health"
            label="Health"
            type="slider"
            min={0}
            max={10}
          />
          <Field
            path="familyMedicalHistory.stressors.finances"
            label="Finances"
            type="slider"
            min={0}
            max={10}
          />
          <Field
            path="familyMedicalHistory.stressors.social"
            label="Social"
            type="slider"
            min={0}
            max={10}
          />
          <div className="flex items-center gap-4">
            <label htmlFor="stressorOther" className="font-medium text-brand-stone-700">
              Other:
            </label>
            <Field
              path="familyMedicalHistory.stressors.other"
              type="text"
              render={({ value, onChange }) => (
                <input
                  id="stressorOther"
                  type="text"
                  value={value || ""}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full px-4 py-2 border border-brand-stone-300 rounded-md focus:ring-2 focus:ring-brand-green-500 bg-white"
                  disabled={!isEditMode}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-brand-stone-700 mb-2 font-medium">
          Family history: Please list relevant family members and health conditions
        </label>
        <Field
          path="familyMedicalHistory.familyHistory"
          render={({ value = [], onChange }) => (
            <>
              {value.map((entry: FamilyHistoryEntry, index: number) => (
                <div
                  key={index}
                  className="flex flex-wrap items-end gap-4 p-4 mb-2 border rounded-lg bg-brand-stone-50"
                >
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-sm font-medium">Family Member</label>
                    <input
                      type="text"
                      value={entry.member}
                      onChange={(e) =>
                        onChange(handleFamilyHistoryChange(value, index, "member", e.target.value))
                      }
                      className="w-full mt-1 p-2 border rounded bg-white"
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="flex-1 min-w-[80px]">
                    <label className="text-sm font-medium">Age(s)</label>
                    <input
                      type="text"
                      value={entry.age}
                      onChange={(e) =>
                        onChange(handleFamilyHistoryChange(value, index, "age", e.target.value))
                      }
                      className="w-full mt-1 p-2 border rounded bg-white"
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium">Health Status</label>
                    <input
                      type="text"
                      value={entry.status}
                      onChange={(e) =>
                        onChange(handleFamilyHistoryChange(value, index, "status", e.target.value))
                      }
                      className="w-full mt-1 p-2 border rounded bg-white"
                      disabled={!isEditMode}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onChange(removeFamilyMember(value, index))}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 h-10"
                    aria-label={`Remove family member ${index + 1}`}
                    disabled={!isEditMode}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => onChange(addFamilyMember(value))}
                className="bg-brand-green-600 text-white px-4 py-2 rounded hover:bg-brand-green-700"
                disabled={!isEditMode}
              >
                + Add Member
              </button>
            </>
          )}
        />
      </div>

      <div className="mt-6 space-y-4">
        <Field
          path="familyMedicalHistory.hospitalized"
          label="Have you ever been hospitalized? If yes, when? Why?"
          type="textarea"
          rows={3}
        />
        
        <Field
          path="familyMedicalHistory.surgeries"
          label="List any surgeries you have had:"
          type="textarea"
          rows={3}
        />
        
        <Field
          path="familyMedicalHistory.vaccinations"
          label="Vaccinations and/or Flu shots? Include approximate age."
          type="textarea"
          rows={3}
        />
        
        <Field
          path="familyMedicalHistory.birthHistory"
          label="How was your birth? (Were you birthed via, C-section, at home, in NICU, preterm etc.)"
          type="textarea"
          rows={3}
        />
        
        <Field
          path="familyMedicalHistory.wasBreastFed"
          label="Were you breast fed?"
          type="radio"
          options={yesNoComboOptions}
        />
        
        <Field
          path="familyMedicalHistory.childHealth"
          label="Health concerns / meds taken as a child/ teen? Please list issues being treated and approximate ages."
          type="textarea"
          rows={3}
        />
        
        <Field
          path="familyMedicalHistory.antibioticsChildAdult"
          label="How often did you take antibiotics as an child/adult? Please list infections treated and date estimates."
          type="textarea"
          rows={3}
        />
      </div>
    </section>
  );
};

export default PastHealthMedicalHistorySectionV2;